import { useLineStore } from "@/stores/lineStore";
import { useCachedArrivalTime } from "./useCachedArrivalTime";
import {
  ArrivalTimeOptionCodeType,
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  RemoveLoadingImage,
  ShowLoadingImage,
} from "@/types";
import { inject, ref } from "vue";
import dayjs from "dayjs";
import { IArrivalTime } from "@/domain/entities/lineInfo/arrivalTime.types";

/** 평균 도착시각, 과거 도착시각 관리 */
export const useArrivalTimes = (type: ArrivalTimeOptionCodeType) => {
  const showLoadingImage = inject(
    PROVIDE_KEY_SHOW_LOADING_IMAGE
  ) as ShowLoadingImage;
  const removeLoadingImage = inject(
    PROVIDE_KEY_REMOVE_LOADING_IMAGE
  ) as RemoveLoadingImage;

  const lineStore = useLineStore();
  const arrivalTimes = ref<IArrivalTime[]>([]);

  const {
    cachedArrivalTimes,
    isFresh,
    getCachedArrivalTime,
    refreshCachedArrivalTimes,
    removeCachedArrivalTime,
  } = useCachedArrivalTime(30, 60 * 10);

  const getArrivalTimes = async (
    lineTurnUid: string,
    busOrder: number,
    date: string,
    cacheId?: string,
    handleSuccess?: () => void,
    handleError?: (title: string, result: any) => void
  ) => {
    const driveDate = dayjs(date).format("YYYY-MM-DD");
    const cacheKey = cacheId ?? driveDate;

    if (isFresh(cacheKey)) {
      arrivalTimes.value = getCachedArrivalTime(cacheKey)?.data ?? [];
      return { isSuccess: true, data: arrivalTimes.value };
    }

    showLoadingImage();

    const result = await lineStore.getBusStopArrivalTimes(
      lineTurnUid,
      busOrder,
      driveDate,
      type
    );

    removeLoadingImage();

    if (!result.isSuccess) {
      handleError && handleError("과거도착시각 조회 오류", result);
      return result;
    }

    removeCachedArrivalTime(cacheKey);
    refreshCachedArrivalTimes(result?.data ?? [], cacheKey);
    arrivalTimes.value = getCachedArrivalTime(cacheKey)?.data ?? [];

    handleSuccess && handleSuccess();
    return result;
  };

  return {
    arrivalTimes,
    cachedArrivalTimes,
    getArrivalTimes,
  };
};
